import { Col, Row } from "antd";
import dayjs from "dayjs";
import { MdClose } from "react-icons/md";
import Button from "shared/components/Button";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import {
  FIELD_TYPES,
  NO_VALUE_OPERATORS,
  OPERATORS_BY_TYPE,
  RANGE_OPERATORS,
  getFieldConfig,
} from "./config";

const datePresets = (t) => [
  { label: t("TODAY"), value: [dayjs(), dayjs()] },
  {
    label: t("YESTERDAY"),
    value: [dayjs().subtract(1, "day"), dayjs().subtract(1, "day")],
  },
  { label: t("LAST_7_DAYS"), value: [dayjs().subtract(7, "day"), dayjs()] },
  { label: t("LAST_30_DAYS"), value: [dayjs().subtract(30, "day"), dayjs()] },
  {
    label: t("THIS_MONTH"),
    value: [dayjs().startOf("month"), dayjs()],
  },
  {
    label: t("LAST_MONTH"),
    value: [
      dayjs().subtract(1, "month").startOf("month"),
      dayjs().subtract(1, "month").endOf("month"),
    ],
  },
];

const FilterRow = ({
  filter,
  index,
  availableFields,
  usedFieldKeys,
  onUpdate,
  onRemove,
  translate,
  selectionOptions,
  fields,
}) => {
  const fieldConfig = filter.fieldKey
    ? getFieldConfig(filter.fieldKey, fields)
    : null;
  const fieldType = fieldConfig?.type;
  const operators = fieldType ? OPERATORS_BY_TYPE[fieldType] || [] : [];
  const isNoValue = NO_VALUE_OPERATORS.includes(filter.operator);
  const isRange = RANGE_OPERATORS.includes(filter.operator);

  const fieldOptions = availableFields
    .filter((f) => f.key === filter.fieldKey || !usedFieldKeys.includes(f.key))
    .map((f) => {
      const Icon = f.icon;
      return {
        label: (
          <span className="advanced-filter__field-option">
            {Icon && <Icon size={14} />}
            {translate.FIELDS[f.labelKey]}
          </span>
        ),
        value: f.key,
      };
    });

  const handleFieldChange = (fieldKey) => {
    const config = getFieldConfig(fieldKey, fields);
    const firstOperator = config
      ? (OPERATORS_BY_TYPE[config.type] || [])[0]?.key
      : undefined;
    onUpdate(index, {
      fieldKey,
      operator: firstOperator,
      value: undefined,
      value2: undefined,
      values: undefined,
    });
  };

  const handleOperatorChange = (operator) => {
    const update = {
      operator,
      value: undefined,
      value2: undefined,
      values: undefined,
    };
    if (fieldType === FIELD_TYPES.DATE) {
      if (isRange && !RANGE_OPERATORS.includes(operator)) {
        update.value = filter.value;
      }
    }
    onUpdate(index, update);
  };

  const renderValueInput = () => {
    if (isNoValue) return null;

    switch (fieldType) {
      case FIELD_TYPES.TEXT:
        return (
          <Input
            placeholder={translate.VALUE_PLACEHOLDER}
            value={filter.value || ""}
            onChange={({ target: { value } }) => onUpdate(index, { value })}
          />
        );

      case FIELD_TYPES.NUMBER:
        if (isRange) {
          return (
            <div className="advanced-filter__range-inputs">
              <div>
                <Input
                  type="number"
                  placeholder="Min"
                  value={
                    filter.value != null ? Number(filter.value) : undefined
                  }
                  label={null}
                  onChange={({ target: { value } }) =>
                    onUpdate(index, {
                      value:
                        value !== "" && value != null
                          ? String(value)
                          : undefined,
                    })
                  }
                />
              </div>
              <div>
                <Input
                  type="number"
                  placeholder="Max"
                  value={
                    filter.value2 != null ? Number(filter.value2) : undefined
                  }
                  label={null}
                  onChange={({ target: { value } }) =>
                    onUpdate(index, {
                      value2:
                        value !== "" && value != null
                          ? String(value)
                          : undefined,
                    })
                  }
                />
              </div>
            </div>
          );
        }
        return (
          <Input
            type="number"
            placeholder={translate.VALUE_PLACEHOLDER}
            value={filter.value != null ? Number(filter.value) : undefined}
            label={null}
            onChange={({ target: { value } }) =>
              onUpdate(index, {
                value:
                  value !== "" && value != null ? String(value) : undefined,
              })
            }
          />
        );

      case FIELD_TYPES.DATE:
        if (isRange) {
          return (
            <Input
              type="date"
              variant="range"
              label={null}
              presets={datePresets((k) => translate.DATE_PRESETS[k])}
              value={
                filter.value && filter.value2
                  ? [dayjs(filter.value), dayjs(filter.value2)]
                  : undefined
              }
              onChange={(dates) => {
                if (dates && dates[0] && dates[1]) {
                  onUpdate(index, {
                    value: dates[0].format("YYYY-MM-DD"),
                    value2: dates[1].format("YYYY-MM-DD"),
                  });
                } else {
                  onUpdate(index, { value: undefined, value2: undefined });
                }
              }}
              format="DD/MM/YYYY"
            />
          );
        }
        return (
          <Input
            type="date"
            variant="simple"
            label={null}
            presets={datePresets((k) => translate.DATE_PRESETS[k]).map((p) => ({
              label: p.label,
              value: p.value[0],
            }))}
            value={filter.value ? dayjs(filter.value) : undefined}
            onChange={(date) => {
              if (date) {
                const formatted = date.format("YYYY-MM-DD");
                onUpdate(index, { value: formatted });
              } else {
                onUpdate(index, { value: undefined });
              }
            }}
            format="DD/MM/YYYY"
          />
        );

      case FIELD_TYPES.SELECTION: {
        const options = selectionOptions[filter.fieldKey] || [];
        return (
          <Select
            label={null}
            mode="multiple"
            placeholder={translate.VALUE_PLACEHOLDER}
            value={filter.values || []}
            onChange={(vals) => onUpdate(index, { values: vals })}
            options={options.map((opt) => ({
              label: opt.label || opt.value,
              value: opt.value,
            }))}
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
          />
        );
      }

      default:
        return null;
    }
  };

  return (
    <Row gutter={16}>
      <Col span={4}>
        <Select
          label={null}
          placeholder={translate.FIELD_PLACEHOLDER}
          value={filter.fieldKey || undefined}
          onChange={handleFieldChange}
          options={fieldOptions}
        />
      </Col>
      <Col span={4}>
        <Select
          label={null}
          placeholder={translate.OPERATOR_PLACEHOLDER}
          value={filter.operator || undefined}
          onChange={handleOperatorChange}
          disabled={!fieldType}
          options={operators.map((op) => ({
            label: translate.OPERATORS[op.labelKey],
            value: op.key,
          }))}
        />
      </Col>
      <Col span={16}>
        <div className="advanced-filter__row-value">
          {renderValueInput()}
          <Button
            type="danger--outlined"
            onClick={() => onRemove(index)}
            className="advanced-filter__remove-btn"
          >
            <MdClose size={16} />
          </Button>
        </div>
      </Col>
    </Row>
  );
};

export default FilterRow;
